import { FC, useEffect, useMemo, useState } from 'react';
import { Role } from '@/models/auth';
import {
  BottomNavigationAction,
  BottomNavigation as BottomNavigationMui,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { routes } from '@/features/navigation/constants';
import { Link, useLocation } from 'react-router';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AddIcon from '@mui/icons-material/Add';
import { theme } from '@/styles/theme';

export const BottomBar: FC<{
  role: Role;
}> = ({ role }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const { pathname } = useLocation();
  const [isAdPartner, setIsAdPartner] = useState(false);

  const { navItems, createAdRoute } = useMemo(() => {
    const baseRoutes = routes[role];
    const isAdPartner = role === Role.AdPartner;
    setIsAdPartner(isAdPartner);

    if (!isAdPartner) {
      return { navItems: baseRoutes, createAdRoute: undefined };
    }

    const createAdRoute = baseRoutes.find((r) => r.to === '/client/create/ad');
    const navItems = [
      ...baseRoutes.filter((r) => r.to !== '/client/create/ad'),
      {
        labelKey: 'menu.wallet',
        to: '/client/wallet',
        Icon: PaidOutlinedIcon,
      },
    ];

    return { navItems, createAdRoute };
  }, [role]);

  useEffect(() => {
    const pathnameIndex = navItems.findIndex((item) => item.to === pathname);
    setValue(pathnameIndex);
  }, [value, pathname]);

  return (
    <div className="fixed bottom-0 left-0 right-0 border-gray-100 border bg-white">
      <div className="pointer-events-auto mx-auto max-w-5xl">
        <div
          className="relative rounded-3xl text-slate-300"
          style={{
            boxShadow: theme.shadows[24],
          }}
        >
          {/* Central create-ad FAB for AdPartner */}
          {isAdPartner && createAdRoute && (
            <Link
              to={createAdRoute.to}
              className="absolute left-1/2 -translate-x-1/2 -top-5"
            >
              <Box
                className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-xl ring-4 ring-[#fc0841]/40 transition-colors"
                sx={{
                  backgroundColor: 'secondary.main',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                }}
              >
                <AddIcon />
              </Box>
            </Link>
          )}

          <BottomNavigationMui
            showLabels
            value={value}
            onChange={(_, newValue) => {
              setValue(newValue);
            }}
            sx={{
              '& .Mui-selected': {
                color: 'secondary.main',
              },
              '& .Mui-selected svg': {
                color: 'secondary.main',
              },
            }}
          >
            {navItems.map(({ to, Icon, labelKey }, index) => (
              <BottomNavigationAction
                key={labelKey}
                component={Link}
                to={to}
                label={t(labelKey)}
                icon={<Icon style={{ fontSize: 30 }} />}
                sx={{
                  marginRight:
                    isAdPartner && index === Math.floor(navItems.length / 2) - 1
                      ? '20px'
                      : 0,
                  marginLeft:
                    isAdPartner && index === Math.floor(navItems.length / 2)
                      ? '20px'
                      : 0,
                }}
              />
            ))}
          </BottomNavigationMui>
        </div>
      </div>
    </div>
  );
};
